import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { formatCLP, formatUF, formatNumber, parseNumber, calculateCursorPosition, formatRUT, parseRUT, validateRUT, formatPhone, parsePhone, validatePhone, validateEmail } from '../utils/formatters';
import { CheckCircle2, AlertCircle, TrendingUp, Mail, Phone, UserIcon, CreditCardIcon, Edit3 } from 'lucide-react';
// Constantes de cálculo
const PLAZO_ANOS = 25;
const PLAZO_MESES = PLAZO_ANOS * 12;
const TASA_ANUAL = 0.045;
const TASA_MENSUAL = TASA_ANUAL / 12;
const CARGA_MAXIMA = 0.3;
const UF_FALLBACK = 39000;
const FACTOR_CUOTA = TASA_MENSUAL === 0 ? PLAZO_MESES > 0 ? 1 / PLAZO_MESES : 0 : TASA_MENSUAL * Math.pow(1 + TASA_MENSUAL, PLAZO_MESES) / (Math.pow(1 + TASA_MENSUAL, PLAZO_MESES) - 1);
// Constantes anti-spam
const MIN_SUBMIT_DELAY = 5000; // 5 segundos entre envíos
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xqavonjd';
export function FlujoPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const calculatorData = location.state;
  // Si no hay datos, redirigir al home
  useEffect(() => {
    if (!calculatorData) {
      navigate('/');
    }
  }, [calculatorData, navigate]);
  // Estados del flujo
  const [showPersonalData, setShowPersonalData] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [showImprove, setShowImprove] = useState(false);
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Anti-spam: honeypot y rate limiting
  const [honeypot, setHoneypot] = useState('');
  const lastSubmitTime = useRef<number>(0);
  const submissionId = useRef<string>('');
  // Datos financieros básicos (iniciales + mejorables)
  const [income, setIncome] = useState(calculatorData?.income || 0);
  const [incomeDisplay, setIncomeDisplay] = useState('');
  const [debt, setDebt] = useState(calculatorData?.debt || 0);
  const [debtDisplay, setDebtDisplay] = useState('');
  const [propertyPrice, setPropertyPrice] = useState(calculatorData?.propertyPrice || 0);
  const [propertyDisplay, setPropertyDisplay] = useState('');
  const [pie, setPie] = useState(calculatorData?.pie || 0);
  const [pieDisplay, setPieDisplay] = useState('');
  // Datos adicionales
  const [bonos, setBonos] = useState(0);
  const [bonosDisplay, setBonosDisplay] = useState('');
  const [inversiones, setInversiones] = useState(0);
  const [inversionesDisplay, setInversionesDisplay] = useState('');
  const [propiedades, setPropiedades] = useState(0);
  const [propiedadesDisplay, setPropiedadesDisplay] = useState('');
  const [vehiculos, setVehiculos] = useState(0);
  const [vehiculosDisplay, setVehiculosDisplay] = useState('');
  // Co-deudor
  const [coDeudorNombre, setCoDeudorNombre] = useState('');
  const [coDeudorRut, setCoDeudorRut] = useState('');
  const [coDeudorRutDisplay, setCoDeudorRutDisplay] = useState('');
  const [coDeudorIncome, setCoDeudorIncome] = useState(0);
  const [coDeudorIncomeDisplay, setCoDeudorIncomeDisplay] = useState('');
  const [coDeudorDebt, setCoDeudorDebt] = useState(0);
  const [coDeudorDebtDisplay, setCoDeudorDebtDisplay] = useState('');
  // Datos personales
  const [nombre, setNombre] = useState('');
  const [rut, setRut] = useState('');
  const [rutDisplay, setRutDisplay] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [touched, setTouched] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Refs para cursor
  const incomeRef = useRef<HTMLInputElement>(null);
  const debtRef = useRef<HTMLInputElement>(null);
  const propertyRef = useRef<HTMLInputElement>(null);
  const pieRef = useRef<HTMLInputElement>(null);
  const bonosRef = useRef<HTMLInputElement>(null);
  const inversionesRef = useRef<HTMLInputElement>(null);
  const propiedadesRef = useRef<HTMLInputElement>(null);
  const vehiculosRef = useRef<HTMLInputElement>(null);
  const coDeudorIncomeRef = useRef<HTMLInputElement>(null);
  const coDeudorDebtRef = useRef<HTMLInputElement>(null);
  // Valor UF
  const [ufHoy, setUfHoy] = useState<number>(() => {
    const cached = localStorage.getItem('uf_cache');
    if (cached) {
      const {
        value,
        timestamp
      } = JSON.parse(cached);
      if (Date.now() - timestamp < 86400000) {
        return value;
      }
    }
    return UF_FALLBACK;
  });
  useEffect(() => {
    const fetchUF = async () => {
      try {
        const response = await fetch('https://mindicador.cl/api/uf');
        const data = await response.json();
        if (data?.serie?.[0]?.valor) {
          const value = data.serie[0].valor;
          setUfHoy(value);
          localStorage.setItem('uf_cache', JSON.stringify({
            value,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.error('Error fetching UF:', error);
      }
    };
    const cached = localStorage.getItem('uf_cache');
    if (!cached) {
      fetchUF();
    }
  }, []);
  // Validaciones de contacto
  const contactErrors = {
    nombre: touched && nombre.trim().length < 2 ? 'Ingresa tu nombre' : '',
    rut: touched && !validateRUT(rut) ? 'RUT inválido' : '',
    email: touched && !validateEmail(email) ? 'Correo inválido' : '',
    telefono: touched && !validatePhone(telefono) ? 'Teléfono inválido' : '',
    terms: touched && !acceptedTerms ? 'Debes aceptar los términos y condiciones' : ''
  };
  const hasContactData = nombre.trim().length >= 2 && validateRUT(rut) && validateEmail(email) && validatePhone(telefono) && acceptedTerms;
  // Handlers de contacto
  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleaned = parseRUT(value);
    setRut(cleaned);
    setRutDisplay(formatRUT(value));
  };
  const handleTelefonoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Solo permitir dígitos y el símbolo +
    const cleaned = value.replace(/[^\d+]/g, '');
    // Guardar el valor tal cual (para que autofill funcione)
    setTelefono(cleaned);
  };
  const handleCoDeudorRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleaned = parseRUT(value);
    setCoDeudorRut(cleaned);
    setCoDeudorRutDisplay(formatRUT(value));
  };
  // Handler genérico para inputs formateados
  const handleFormattedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, ref: React.RefObject<HTMLInputElement>, oldDisplay: string, setNumeric: (n: number) => void, setDisplay: (s: string) => void) => {
    const input = e.target;
    const cursorStart = input.selectionStart || 0;
    const cursorEnd = input.selectionEnd || 0;
    const newRawValue = input.value;
    if (newRawValue.trim() === '') {
      setNumeric(0);
      setDisplay('');
      return;
    }
    const numericValue = parseNumber(newRawValue);
    setNumeric(numericValue);
    const formatted = formatNumber(numericValue);
    setDisplay(formatted);
    requestAnimationFrame(() => {
      if (ref.current) {
        const newPosition = calculateCursorPosition(oldDisplay, formatted, cursorStart, cursorEnd);
        ref.current.setSelectionRange(newPosition, newPosition);
      }
    });
  }, []);
  // Cálculos
  const calculations = useMemo(() => {
    const ingresoTotal = Math.max(income + bonos + coDeudorIncome, 0);
    const deudaTotal = Math.max(debt + coDeudorDebt, 0);
    const loadRatio = ingresoTotal > 0 ? deudaTotal / ingresoTotal : 1;
    const piePct = propertyPrice > 0 ? pie / propertyPrice * 100 : 0;
    const creditNeeded = Math.max(0, propertyPrice - pie);
    const maxCuotaCLP = Math.max(0, ingresoTotal * CARGA_MAXIMA - deudaTotal);
    const maxCreditoUFByCuota = maxCuotaCLP > 0 && FACTOR_CUOTA > 0 && ufHoy > 0 ? maxCuotaCLP / ufHoy / FACTOR_CUOTA : 0;
    const cuotaPropiedadCLP = creditNeeded > 0 && FACTOR_CUOTA > 0 ? creditNeeded * ufHoy * FACTOR_CUOTA : 0;
    const dividendoRatio = ingresoTotal > 0 && cuotaPropiedadCLP > 0 ? cuotaPropiedadCLP / ingresoTotal : 0;
    return {
      ingresoTotal,
      deudaTotal,
      loadRatio,
      piePct,
      creditNeeded,
      maxCuotaCLP,
      maxCreditoUFByCuota,
      cuotaPropiedadCLP,
      dividendoRatio
    };
  }, [income, bonos, coDeudorIncome, debt, coDeudorDebt, pie, propertyPrice, ufHoy]);
  // Score
  const score = useMemo(() => {
    const {
      piePct,
      loadRatio,
      dividendoRatio,
      cuotaPropiedadCLP,
      ingresoTotal
    } = calculations;
    let s = 0;
    if (piePct >= 30) s += 40;else if (piePct >= 20) s += 20;else if (piePct >= 15) s -= 10;else if (piePct >= 10) s -= 25;else if (piePct > 0) s -= 40;
    if (loadRatio <= 0.15) s += 25;else if (loadRatio <= 0.25) s += 10;else if (loadRatio <= 0.35) s += 0;else if (loadRatio <= 0.45) s -= 15;else s -= 30;
    if (cuotaPropiedadCLP > 0 && ingresoTotal > 0) {
      if (dividendoRatio < 0.2) s += 25;else if (dividendoRatio <= 0.25) s += 10;else if (dividendoRatio <= 0.3) s += 0;else if (dividendoRatio <= 0.35) s -= 20;else s -= 35;
    }
    if (coDeudorIncome > 0) s += 10;
    const patrimonioTotal = inversiones + propiedades + vehiculos;
    if (patrimonioTotal > 0) {
      if (patrimonioTotal >= 10000000) s += 10;else if (patrimonioTotal >= 5000000) s += 5;else if (patrimonioTotal >= 2000000) s += 3;
    }
    return Math.max(0, Math.min(100, Math.round(s)));
  }, [calculations, coDeudorIncome, inversiones, propiedades, vehiculos]);
  // Probabilidad
  const probability = useMemo(() => {
    if (score >= 85) return {
      label: 'Muy alta',
      color: 'bg-green-600'
    };
    if (score >= 70) return {
      label: 'Alta',
      color: 'bg-green-500'
    };
    if (score >= 55) return {
      label: 'Media',
      color: 'bg-yellow-500'
    };
    if (score >= 40) return {
      label: 'Baja',
      color: 'bg-orange-500'
    };
    return {
      label: 'Muy baja',
      color: 'bg-red-500'
    };
  }, [score]);
  // Bancos - mantener para payload de Formspree pero no mostrar en UI
  const bancos = useMemo(() => {
    if (score >= 70) return ['Banco Santander', 'BCI', 'Itaú'];
    if (score >= 55) return ['Scotiabank', 'Banco de Chile'];
    if (score >= 40) return ['BancoEstado'];
    return ['Revisar caso antes de postular'];
  }, [score]);
  // Función helper para limpiar payload (sin null, undefined, o strings vacías)
  const cleanPayload = (obj: Record<string, any>): Record<string, string> => {
    const cleaned: Record<string, string> = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        cleaned[key] = String(value);
      }
    });
    return cleaned;
  };
  // Función helper para rate limiting
  const canSubmit = (): boolean => {
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTime.current;
    return timeSinceLastSubmit >= MIN_SUBMIT_DELAY;
  };
  // Función helper para enviar a Formspree con protecciones anti-spam
  const submitToFormspree = async (payload: Record<string, any>, type: 'initial' | 'improved'): Promise<boolean> => {
    // Protección 1: Honeypot
    if (honeypot !== '') {
      console.warn('Honeypot triggered - submission blocked');
      return false;
    }
    // Protección 2: Rate limiting
    if (!canSubmit()) {
      console.warn('Rate limit - please wait before submitting again');
      return false;
    }
    // Protección 3: Prevenir duplicados
    if (type === 'initial' && submissionId.current !== '') {
      console.warn('Initial submission already sent');
      return false;
    }
    setIsSubmitting(true);
    try {
      // Limpiar payload (sin null, undefined, strings vacías)
      const cleanedPayload = cleanPayload({
        // Campo email correctamente estructurado
        email: email.trim(),
        _replyto: email.trim(),
        _subject: payload._subject,
        // Datos de contacto
        nombre: nombre.trim(),
        rut: formatRUT(rut),
        telefono: formatPhone(telefono),
        // Datos financieros
        renta_liquida: formatCLP(income),
        deudas_mensuales: formatCLP(debt),
        precio_propiedad: formatUF(propertyPrice),
        pie_disponible: formatUF(pie),
        // Datos adicionales (solo si existen)
        ...(bonos > 0 && {
          bonos_mensuales: formatCLP(bonos)
        }),
        ...(inversiones > 0 && {
          inversiones: formatCLP(inversiones)
        }),
        ...(propiedades > 0 && {
          propiedades: formatCLP(propiedades)
        }),
        ...(vehiculos > 0 && {
          vehiculos: formatCLP(vehiculos)
        }),
        // Co-deudor (solo si existe)
        ...(coDeudorIncome > 0 && {
          codeudor_nombre: coDeudorNombre,
          codeudor_rut: formatRUT(coDeudorRut),
          codeudor_renta: formatCLP(coDeudorIncome),
          codeudor_deudas: formatCLP(coDeudorDebt)
        }),
        // Resultados
        score: `${score}/100`,
        probabilidad: probability.label,
        bancos: bancos.join(', '),
        dividendo_estimado: formatCLP(Math.round(calculations.cuotaPropiedadCLP)),
        porcentaje_pie: `${calculations.piePct.toFixed(1)}%`,
        // Metadata
        fecha_hora: new Date().toLocaleString('es-CL'),
        tipo_evaluacion: type === 'initial' ? 'Evaluación Inicial' : 'Evaluación Mejorada',
        origen: 'Flujo de Evaluación Creditual',
        ...(submissionId.current && {
          submission_id: submissionId.current
        })
      });
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(cleanedPayload)
      });
      if (!response.ok) {
        throw new Error(`Formspree error: ${response.status}`);
      }
      // Actualizar rate limiting
      lastSubmitTime.current = Date.now();
      // Generar submission ID para la primera evaluación
      if (type === 'initial' && !submissionId.current) {
        submissionId.current = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
      return true;
    } catch (error) {
      console.error('Error submitting to Formspree:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  // Handler para ver score
  const handleVerScore = async () => {
    setTouched(true);
    const hasSyncErrors = Object.values(contactErrors).some(Boolean);
    if (hasSyncErrors || isSubmitting) return;
    const success = await submitToFormspree({
      _subject: `Nueva Evaluación Creditual - ${nombre.trim()}`
    }, 'initial');
    if (success) {
      setShowPersonalData(false);
      setShowResults(true);
    }
  };
  // Handler para mejorar evaluación
  const handleMejorarEvaluacion = () => {
    setShowImprove(true);
  };
  // Handler para enviar evaluación mejorada
  const handleEnviarMejorada = async () => {
    if (isSubmitting) return;
    const success = await submitToFormspree({
      _subject: `Evaluación Mejorada - ${nombre.trim()} (Score: ${score})`
    }, 'improved');
    if (success) {
      setSent(true);
    }
  };
  // Pre-cargar displays cuando showImprove se activa
  useEffect(() => {
    if (showImprove && income > 0 && incomeDisplay === '') {
      setIncomeDisplay(formatNumber(income));
    }
    if (showImprove && debt >= 0 && debtDisplay === '') {
      setDebtDisplay(formatNumber(debt));
    }
    if (showImprove && propertyPrice > 0 && propertyDisplay === '') {
      setPropertyDisplay(formatNumber(propertyPrice));
    }
    if (showImprove && pie > 0 && pieDisplay === '') {
      setPieDisplay(formatNumber(pie));
    }
  }, [showImprove, income, debt, propertyPrice, pie, incomeDisplay, debtDisplay, propertyDisplay, pieDisplay]);
  if (!calculatorData) {
    return null;
  }
  if (sent) {
    return <div className="min-h-screen flex flex-col bg-white" data-id="element-166">
        <Header data-id="element-167" />
        <div className="flex-1 py-12 flex items-center justify-center" data-id="element-168">
          <div className="container mx-auto max-w-2xl px-4" data-id="element-169">
            <div className="bg-white border-2 border-[#2B5FD9] rounded-2xl p-8 shadow-xl text-center" data-id="element-170">
              <div className="mb-6 flex justify-center" data-id="element-171">
                <div className="rounded-full bg-[#2B5FD9]/10 p-4" data-id="element-172">
                  <CheckCircle2 className="h-12 w-12 text-[#2B5FD9]" data-id="element-173" />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-4" data-id="element-174">
                ¡Listo, {nombre}!
              </h3>
              <p className="text-lg text-[#6B7280] mb-2" data-id="element-175">
                Recibimos tu evaluación completa.
              </p>
              <p className="text-base text-[#6B7280] mb-6" data-id="element-176">
                Te contactaremos a <strong data-id="element-177">{email}</strong> para gestionar tu
                aprobación con las instituciones más compatibles.
              </p>
              <div className="bg-[#E8EDF5] rounded-xl p-4" data-id="element-178">
                <p className="text-sm text-[#6B7280]" data-id="element-179">
                  Tu score:{' '}
                  <strong className="text-[#0A1628]" data-id="element-180">{score}/100</strong> ·
                  Probabilidad:{' '}
                  <strong className="text-[#0A1628]" data-id="element-181">
                    {probability.label}
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer data-id="element-182" />
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-183">
      <Header data-id="element-184" />

      <div className="flex-1 py-8 md:py-12" data-id="element-185">
        <div className="container mx-auto max-w-4xl px-4" data-id="element-186">
          {/* Datos Personales */}
          {showPersonalData && <>
              <div className="text-center mb-6" data-id="element-187">
                <h1 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-2" data-id="element-188">
                  Ahora necesitamos tus datos de contacto
                </h1>
                <p className="text-sm md:text-base text-[#6B7280]" data-id="element-189">
                  Para calcular tu score y enviarte los resultados
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 mb-6" data-id="element-190">
                <h3 className="text-lg font-bold text-[#0A1628] mb-4" data-id="element-191">
                  Tus datos de contacto
                </h3>
                <div className="space-y-4" data-id="element-192">
                  {/* Honeypot field - invisible para usuarios reales */}
                  <input type="text" name="_gotcha" value={honeypot} onChange={e => setHoneypot(e.target.value)} style={{
                display: 'none'
              }} tabIndex={-1} autoComplete="off" data-id="element-193" />

                  <div data-id="element-194">
                    <label className="block text-sm font-medium mb-2" data-id="element-195">
                      Nombre completo *
                    </label>
                    <div className="relative" data-id="element-196">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-197" />
                      <input type="text" name="name" autoComplete="name" value={nombre} onChange={e => setNombre(e.target.value)} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="Tu nombre y apellido" data-id="element-198" />
                    </div>
                    {contactErrors.nombre && <p className="mt-1 text-xs text-red-600" data-id="element-199">
                        {contactErrors.nombre}
                      </p>}
                  </div>

                  <div data-id="element-200">
                    <label className="block text-sm font-medium mb-2" data-id="element-201">
                      RUT *
                    </label>
                    <div className="relative" data-id="element-202">
                      <CreditCardIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-203" />
                      <input type="text" name="tax-id" autoComplete="off" value={rutDisplay} onChange={handleRutChange} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="12.345.678-9" data-id="element-204" />
                    </div>
                    {contactErrors.rut && <p className="mt-1 text-xs text-red-600" data-id="element-205">
                        {contactErrors.rut}
                      </p>}
                  </div>

                  <div data-id="element-206">
                    <label className="block text-sm font-medium mb-2" data-id="element-207">
                      Correo electrónico *
                    </label>
                    <div className="relative" data-id="element-208">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-209" />
                      <input type="email" name="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="tu@correo.cl" data-id="element-210" />
                    </div>
                    {contactErrors.email && <p className="mt-1 text-xs text-red-600" data-id="element-211">
                        {contactErrors.email}
                      </p>}
                  </div>

                  <div data-id="element-212">
                    <label className="block text-sm font-medium mb-2" data-id="element-213">
                      Teléfono *
                    </label>
                    <div className="relative" data-id="element-214">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-215" />
                      <input type="tel" name="tel" autoComplete="tel" value={telefono} onChange={handleTelefonoChange} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="+56912345678" data-id="element-216" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-217">
                      Ingresa tu número con +56
                    </p>
                    {contactErrors.telefono && <p className="mt-1 text-xs text-red-600" data-id="element-218">
                        {contactErrors.telefono}
                      </p>}
                  </div>

                  {/* Checkbox de Términos y Condiciones */}
                  <div className="pt-2" data-id="element-219">
                    <label className="flex items-start gap-3 cursor-pointer group" data-id="element-220">
                      <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} onBlur={() => setTouched(true)} className="mt-1 w-4 h-4 text-[#2B5FD9] border-gray-300 rounded focus:ring-2 focus:ring-[#2B5FD9]/20 cursor-pointer" data-id="element-221" />
                      <span className="text-sm text-[#6B7280] leading-relaxed" data-id="element-222">
                        Acepto los{' '}
                        <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-[#2B5FD9] hover:underline font-medium" onClick={e => e.stopPropagation()} data-id="element-223">
                          Términos y Condiciones
                        </a>{' '}
                        y autorizo el tratamiento de mis datos personales
                        conforme a la{' '}
                        <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[#2B5FD9] hover:underline font-medium" onClick={e => e.stopPropagation()} data-id="element-224">
                          Política de Privacidad
                        </a>
                        . *
                      </span>
                    </label>
                    {contactErrors.terms && <p className="mt-2 text-xs text-red-600 ml-7" data-id="element-225">
                        {contactErrors.terms}
                      </p>}
                  </div>
                </div>
              </div>

              <button onClick={handleVerScore} disabled={!hasContactData || isSubmitting} className="w-full px-6 py-4 bg-[#2B5FD9] text-white font-semibold rounded-lg hover:bg-[#1e4bb8] transition disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-226">
                {isSubmitting ? 'Enviando...' : 'Ver mi score hipotecario →'}
              </button>
            </>}

          {/* Resultados */}
          {showResults && <>
              <div className="text-center mb-6" data-id="element-227">
                <h1 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-2" data-id="element-228">
                  Tu evaluación Creditual
                </h1>
                <p className="text-sm md:text-base text-[#6B7280]" data-id="element-229">
                  Resultados basados en tus datos financieros
                </p>
              </div>

              {/* Score y Probabilidad */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6" data-id="element-230">
                <div className="text-center mb-6" data-id="element-231">
                  <p className="text-sm text-[#6B7280] mb-1" data-id="element-232">
                    Probabilidad de aprobación
                  </p>
                  <div className="flex items-center justify-center gap-3" data-id="element-233">
                    <div className={`w-3 h-3 rounded-full ${probability.color}`} data-id="element-234" />
                    <p className="text-xl md:text-2xl font-bold text-[#0A1628]" data-id="element-235">
                      {probability.label}
                    </p>
                  </div>
                  <p className="text-xs text-[#6B7280] mt-2" data-id="element-236">
                    Score: {score}/100
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6" data-id="element-237">
                  <div data-id="element-238">
                    <p className="text-sm text-[#6B7280] mb-1" data-id="element-239">Pie</p>
                    <p className="text-lg font-bold text-[#0A1628]" data-id="element-240">
                      {calculations.piePct.toFixed(1)}%
                    </p>
                  </div>
                  <div data-id="element-241">
                    <p className="text-sm text-[#6B7280] mb-1" data-id="element-242">
                      Dividendo estimado
                    </p>
                    <p className="text-lg font-bold text-[#0A1628]" data-id="element-243">
                      {formatCLP(Math.round(calculations.cuotaPropiedadCLP))}
                    </p>
                  </div>
                  <div data-id="element-244">
                    <p className="text-sm text-[#6B7280] mb-1" data-id="element-245">Monto máximo</p>
                    <p className="text-lg font-bold text-[#0A1628]" data-id="element-246">
                      {formatUF(Math.round(calculations.maxCreditoUFByCuota))}
                    </p>
                  </div>
                </div>

                {score < 80 && <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4" data-id="element-247">
                    <div className="flex items-start gap-3" data-id="element-248">
                      <AlertCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" data-id="element-249" />
                      <div data-id="element-250">
                        <p className="font-semibold text-orange-800 mb-1" data-id="element-251">
                          Tu score está por debajo de 80 puntos
                        </p>
                        <p className="text-sm text-orange-700" data-id="element-252">
                          Puedes mejorar tus probabilidades ajustando tus datos
                          financieros, agregando patrimonio o incluyendo un
                          co-deudor.
                        </p>
                      </div>
                    </div>
                  </div>}

                {!showImprove && <button onClick={handleMejorarEvaluacion} className="w-full px-6 py-3 bg-[#2B5FD9] text-white font-semibold rounded-lg hover:bg-[#1e4bb8] transition" data-id="element-253">
                    Mejorar mi evaluación →
                  </button>}
              </div>

              {/* Panel de Mejora */}
              {showImprove && <div className="space-y-6" data-id="element-254">
                  {/* Score Destacado */}
                  <div className="bg-gradient-to-br from-[#2B5FD9] to-[#1E4BB8] rounded-2xl p-6 text-white sticky top-4 z-10 shadow-lg" data-id="element-255">
                    <div className="flex items-center justify-between" data-id="element-256">
                      <div data-id="element-257">
                        <p className="text-sm opacity-90 mb-1" data-id="element-258">Score actual</p>
                        <p className="text-5xl font-bold" data-id="element-259">{score}</p>
                        <p className="text-sm opacity-90 mt-1" data-id="element-260">
                          Probabilidad: {probability.label}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-261">
                        <p className="text-sm opacity-90 mb-1" data-id="element-262">Objetivo</p>
                        <p className="text-3xl font-bold" data-id="element-263">80+</p>
                        {score >= 80 && <CheckCircle2 className="h-8 w-8 mt-2 ml-auto" data-id="element-264" />}
                      </div>
                    </div>
                  </div>

                  {/* Datos Financieros Básicos */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-265">
                    <div className="flex items-center gap-2 mb-4" data-id="element-266">
                      <Edit3 className="h-5 w-5 text-[#2B5FD9]" data-id="element-267" />
                      <h3 className="text-lg font-bold text-[#0A1628]" data-id="element-268">
                        Datos Financieros Básicos
                      </h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4" data-id="element-269">
                      <div data-id="element-270">
                        <label className="block text-sm font-medium mb-2" data-id="element-271">
                          Renta líquida mensual (CLP)
                        </label>
                        <input ref={incomeRef} type="text" inputMode="numeric" value={incomeDisplay} onChange={e => handleFormattedChange(e, incomeRef, incomeDisplay, setIncome, setIncomeDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="1.200.000" data-id="element-272" />
                      </div>
                      <div data-id="element-273">
                        <label className="block text-sm font-medium mb-2" data-id="element-274">
                          Deudas mensuales (CLP)
                        </label>
                        <input ref={debtRef} type="text" inputMode="numeric" value={debtDisplay} onChange={e => handleFormattedChange(e, debtRef, debtDisplay, setDebt, setDebtDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="250.000" data-id="element-275" />
                      </div>
                      <div data-id="element-276">
                        <label className="block text-sm font-medium mb-2" data-id="element-277">
                          Precio de la propiedad (UF)
                        </label>
                        <input ref={propertyRef} type="text" inputMode="numeric" value={propertyDisplay} onChange={e => handleFormattedChange(e, propertyRef, propertyDisplay, setPropertyPrice, setPropertyDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="3.000" data-id="element-278" />
                      </div>
                      <div data-id="element-279">
                        <label className="block text-sm font-medium mb-2" data-id="element-280">
                          Pie disponible (UF)
                        </label>
                        <input ref={pieRef} type="text" inputMode="numeric" value={pieDisplay} onChange={e => handleFormattedChange(e, pieRef, pieDisplay, setPie, setPieDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="600" data-id="element-281" />
                      </div>
                    </div>
                  </div>

                  {/* Ingresos Adicionales */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-282">
                    <h3 className="text-lg font-bold text-[#0A1628] mb-4" data-id="element-283">
                      Ingresos Adicionales
                    </h3>
                    <div className="space-y-4" data-id="element-284">
                      <div data-id="element-285">
                        <label className="block text-sm font-medium mb-2" data-id="element-286">
                          Bonos mensuales (CLP)
                        </label>
                        <input ref={bonosRef} type="text" inputMode="numeric" value={bonosDisplay} onChange={e => handleFormattedChange(e, bonosRef, bonosDisplay, setBonos, setBonosDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="0" data-id="element-287" />
                        <p className="text-xs text-[#6B7280] mt-1" data-id="element-288">
                          Bonos de producción, comisiones, etc.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Patrimonio */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-289">
                    <h3 className="text-lg font-bold text-[#0A1628] mb-4" data-id="element-290">
                      Patrimonio
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4" data-id="element-291">
                      <div data-id="element-292">
                        <label className="block text-sm font-medium mb-2" data-id="element-293">
                          Inversiones (CLP)
                        </label>
                        <input ref={inversionesRef} type="text" inputMode="numeric" value={inversionesDisplay} onChange={e => handleFormattedChange(e, inversionesRef, inversionesDisplay, setInversiones, setInversionesDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="0" data-id="element-294" />
                        <p className="text-xs text-[#6B7280] mt-1" data-id="element-295">
                          APV, fondos mutuos, acciones
                        </p>
                      </div>
                      <div data-id="element-296">
                        <label className="block text-sm font-medium mb-2" data-id="element-297">
                          Propiedades (CLP)
                        </label>
                        <input ref={propiedadesRef} type="text" inputMode="numeric" value={propiedadesDisplay} onChange={e => handleFormattedChange(e, propiedadesRef, propiedadesDisplay, setPropiedades, setPropiedadesDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="0" data-id="element-298" />
                        <p className="text-xs text-[#6B7280] mt-1" data-id="element-299">
                          Valor estimado total
                        </p>
                      </div>
                      <div data-id="element-300">
                        <label className="block text-sm font-medium mb-2" data-id="element-301">
                          Vehículos (CLP)
                        </label>
                        <input ref={vehiculosRef} type="text" inputMode="numeric" value={vehiculosDisplay} onChange={e => handleFormattedChange(e, vehiculosRef, vehiculosDisplay, setVehiculos, setVehiculosDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="0" data-id="element-302" />
                        <p className="text-xs text-[#6B7280] mt-1" data-id="element-303">
                          Valor estimado total
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Co-deudor */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-304">
                    <h3 className="text-lg font-bold text-[#0A1628] mb-4" data-id="element-305">
                      Co-deudor
                    </h3>
                    <div className="space-y-4" data-id="element-306">
                      <div className="grid md:grid-cols-2 gap-4" data-id="element-307">
                        <div data-id="element-308">
                          <label className="block text-sm font-medium mb-2" data-id="element-309">
                            Nombre del co-deudor
                          </label>
                          <input type="text" value={coDeudorNombre} onChange={e => setCoDeudorNombre(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="Nombre completo" data-id="element-310" />
                        </div>
                        <div data-id="element-311">
                          <label className="block text-sm font-medium mb-2" data-id="element-312">
                            RUT del co-deudor
                          </label>
                          <input type="text" value={coDeudorRutDisplay} onChange={handleCoDeudorRutChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="12.345.678-9" data-id="element-313" />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4" data-id="element-314">
                        <div data-id="element-315">
                          <label className="block text-sm font-medium mb-2" data-id="element-316">
                            Renta líquida mensual (CLP)
                          </label>
                          <input ref={coDeudorIncomeRef} type="text" inputMode="numeric" value={coDeudorIncomeDisplay} onChange={e => handleFormattedChange(e, coDeudorIncomeRef, coDeudorIncomeDisplay, setCoDeudorIncome, setCoDeudorIncomeDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="0" data-id="element-317" />
                        </div>
                        <div data-id="element-318">
                          <label className="block text-sm font-medium mb-2" data-id="element-319">
                            Deudas mensuales (CLP)
                          </label>
                          <input ref={coDeudorDebtRef} type="text" inputMode="numeric" value={coDeudorDebtDisplay} onChange={e => handleFormattedChange(e, coDeudorDebtRef, coDeudorDebtDisplay, setCoDeudorDebt, setCoDeudorDebtDisplay)} className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="0" data-id="element-320" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resumen de Cálculos */}
                  <div className="bg-[#E8EDF5] rounded-xl p-6" data-id="element-321">
                    <h3 className="text-lg font-bold text-[#0A1628] mb-4" data-id="element-322">
                      Resumen Actualizado
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4" data-id="element-323">
                      <div data-id="element-324">
                        <p className="text-sm text-[#6B7280] mb-1" data-id="element-325">
                          Ingreso total
                        </p>
                        <p className="text-xl font-bold text-[#0A1628]" data-id="element-326">
                          {formatCLP(calculations.ingresoTotal)}
                        </p>
                      </div>
                      <div data-id="element-327">
                        <p className="text-sm text-[#6B7280] mb-1" data-id="element-328">
                          Deuda total
                        </p>
                        <p className="text-xl font-bold text-[#0A1628]" data-id="element-329">
                          {formatCLP(calculations.deudaTotal)}
                        </p>
                      </div>
                      <div data-id="element-330">
                        <p className="text-sm text-[#6B7280] mb-1" data-id="element-331">Pie</p>
                        <p className="text-xl font-bold text-[#0A1628]" data-id="element-332">
                          {calculations.piePct.toFixed(1)}%
                        </p>
                      </div>
                      <div data-id="element-333">
                        <p className="text-sm text-[#6B7280] mb-1" data-id="element-334">
                          Dividendo estimado
                        </p>
                        <p className="text-xl font-bold text-[#0A1628]" data-id="element-335">
                          {formatCLP(Math.round(calculations.cuotaPropiedadCLP))}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Botón de envío */}
                  <button onClick={handleEnviarMejorada} disabled={isSubmitting} className="w-full px-6 py-4 bg-[#2B5FD9] text-white font-semibold rounded-lg hover:bg-[#1e4bb8] transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-336">
                    {isSubmitting ? 'Enviando...' : 'Enviar evaluación mejorada →'}
                  </button>
                </div>}
            </>}
        </div>
      </div>

      <Footer data-id="element-337" />
    </div>;
}
export default FlujoPage;